"use client"

import { openAiChat, closeAiChat } from "library/aiChatService"
import { MINT_AI_CHAT_ORIGIN } from "scripts/mintAiWidgetInit"
import { useEffect, useCallback, useRef, useState } from "react"

const BUTTON_SIZE = 56

function isChatVisible(): boolean {
  const iframe = document.querySelector("iframe[src*='chat']") as HTMLIFrameElement | null
  if (!iframe) return false
  const bottom = parseInt(iframe.style.bottom, 10)
  return !isNaN(bottom) && bottom > -100
}

export default function MintAiChatButton() {
  const [isOpen, setIsOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  const syncState = useCallback(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setIsOpen(isChatVisible())
    }, 150)
  }, [])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== MINT_AI_CHAT_ORIGIN) return
      const type = e.data?.type
      if (type === "chatClosed" || type === "close" || type === "chatEnd") {
        setIsOpen(false)
      }
      if (type === "chatOpened") {
        setIsOpen(true)
      }
    }
    window.addEventListener("message", onMessage)

    const observer = new MutationObserver(() => syncState())
    const watchIframe = () => {
      const iframe = document.querySelector("iframe[src*='chat']")
      if (iframe) {
        observer.observe(iframe, { attributes: true, attributeFilter: ["style"] })
      }
    }

    watchIframe()
    const retryTimer = setTimeout(watchIframe, 2000)

    return () => {
      window.removeEventListener("message", onMessage)
      observer.disconnect()
      clearTimeout(retryTimer)
      clearTimeout(debounceRef.current)
    }
  }, [syncState])

  const handleClick = async () => {
    if (isOpen) {
      closeAiChat()
      setIsOpen(false)
    } else {
      await openAiChat()
      setIsOpen(true)
    }
  }

  return (
    <button
      onClick={handleClick}
      aria-label={isOpen ? "סגור צ'אט" : "פתח צ'אט AI"}
      style={{
        position: "fixed",
        bottom: 20,
        left: 20,
        zIndex: 99999998,
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        borderRadius: "50%",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isOpen
          ? "#e0e0e0"
          : "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(240,240,240,0.95) 100%)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        padding: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.08)"
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.25)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)"
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.2)"
      }}
    >
      {isOpen ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6l12 12"
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 2C6.48 2 2 5.82 2 10.5c0 2.55 1.35 4.85 3.5 6.42V22l4.06-2.23c.78.15 1.6.23 2.44.23 5.52 0 10-3.82 10-8.5S17.52 2 12 2z"
            fill="#333"
          />
          <circle cx="8" cy="10.5" r="1.2" fill="white" />
          <circle cx="12" cy="10.5" r="1.2" fill="white" />
          <circle cx="16" cy="10.5" r="1.2" fill="white" />
        </svg>
      )}
    </button>
  )
}
